// CallRecordTab.tsx · v12.10.17 (#1723 T120 follow-up: Copy names the caller type and request from Personalization - never the stored key) · 2026-10-16 · VoiceSpectrum AI
// CallRecordTab.tsx · v12.10.15 (#1627 practice name falls back to the session; #1636 recordText uses the shared callRecordText) · 2026-10-13 · VoiceSpectrum AI
// CallRecordTab.tsx · v12.10.14 (#1620 T88, UI-PROTO-CALL-RECORD-PRINT v1.9 LOCKED: One call and Full page per call draw the shared
//   CallRecordSheet - the list card on top, What the caller needs as rows, Call data in two columns, What the practice did, transcript from
//   page 2; no "handle per practice policy" note; footer REPORT_STANDARD v1.2 with "Contains patient information") · 2026-10-12 · VoiceSpectrum AI
// CallRecordTab.tsx · v12.10.13 (#1564: opened from the message drawer's Print ▾ - ?call=<id>&do=print|pdf picks that call, shows it and
//   opens the print window once the full record is read; the same audit as the Print button) · 2026-10-10 · VoiceSpectrum AI
// CallRecordTab.tsx · v12.10.13 (#1603 T88: UI-PROTO-CALL-RECORD v1.1 + REPORT_STANDARD v1.0 - one FILTERS panel (One call / Date range,
//   find with a pick list; range = the shared quick picks + Custom, Assigned to, Status, Layout List / Full page per call; Include ▾
//   with the 7 parts, Transcript off; Show with the white dot), actions Print · Export ▾ (PDF, Excel for a range, HTML) · Copy, the
//   patient-information notice, REPORT divider with As printed / As copied, REPORT card; print header (PERIOD, RUN, filter line only
//   for non-default Assigned / Status / Include, "Contains patient information" line), running header page 2+, Full = one call per
//   page, footer Printed · Generated by VoiceSpectrum AI · Page X of Y; "No calls for …". Same fetches, audit and Excel as 12.10.12) · 2026-10-10 · VoiceSpectrum AI
// CallRecordTab.tsx · v12.10.12 (#1550 T81, UI-PROTO-CALL-RECORD v1.0 LOCKED 10/10) · 2026-10-10 · VoiceSpectrum AI
// Reports › Call record: print, PDF or copy the call record - the drawer as it is - for ONE call or a DATE RANGE (a full page
// per call, or a list). Tick what to include: what the caller needs, caller details, status / assigned / urgent, labels,
// reminder, notes, transcript (off by default). Excel for a range. Every print, PDF, copy, HTML and Excel is written to the
// audit log - these records hold patient information. The drawer link comes later (S91), not in this sprint.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type DateScope } from '@shared-ui/DateScope';
import {
  PrintFooter, PrintHeader, ReportDates, ReportDivider, ReportEmpty, ReportMenu, ShowButton, etTime, exportHtml, fileSafe, printReport,
  scopeKey, scopeWindow, useBeforePrint, type ReportWindow,
} from '@shared-ui/ReportFrame';
import api from '../../lib/api';
import { postAndSave } from '../../lib/reportDownload';
import { toast } from '../../lib/toast';
import { formatReference } from '../MessagesPage';
import { CallRecordSheet, callRecordText, namedRecord, type CrsRecord, sessionPracticeName } from './CallRecordSheet';
import { type TaxonomyItem } from '../../lib/taxonomy';   // v12.10.17 (#1723 T120 follow-up)

interface Note { id: string; author: string; at: string; text: string; kind?: string | null }
interface Line { ts: string | null; speaker: string; role: 'agent' | 'caller' | 'path'; text: string }
interface Field { token: string; label: string; value: string }
interface Row {
  id: string; referenceNo: string | null; name: string; phone: string; language: string; callerType: string; status: string; priority: string; category: string;
  labels: string[]; summary: string; aiSummary: string; startedAt: string; durationSeconds: number; reminder: string | null; reminderDue?: string | null;
  assignedTo: string | null; assignedToUserId?: number | null; chartNumber: string | null; notes: Note[]; transcript: Line[]; capturedFields?: Field[]; setupTestCall?: boolean;
}
interface Envelope { messages: Row[]; availableLabels?: { name: string; color: string }[] }

type Part = 'summary' | 'caller' | 'status' | 'labels' | 'reminder' | 'notes' | 'transcript';
const PARTS: { key: Part; label: string; hint?: string }[] = [
  { key: 'summary', label: 'What the caller needs', hint: 'summary' },
  { key: 'caller', label: 'Caller details', hint: 'name, phone, DOB, chart #' },
  { key: 'status', label: 'Status, assigned, urgent' },
  { key: 'labels', label: 'Labels' },
  { key: 'reminder', label: 'Reminder' },
  { key: 'notes', label: 'Notes' },
  { key: 'transcript', label: 'Transcript', hint: 'off by default' },
];
const DEFAULT_PARTS: Part[] = ['summary', 'caller', 'status', 'labels', 'reminder', 'notes'];
const MAX_CALLS = 100;

const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const statusWord = (s: string) => (s === 'done' ? 'Completed' : s === 'prog' ? 'In progress' : 'Pending');
const langWord = (l: string) => ({ en: 'English', es: 'Spanish', fr: 'French', de: 'German' } as Record<string, string>)[(l || '').slice(0, 2).toLowerCase()] ?? l;
const fmtPhone = (p: string) => { const d = (p || '').replace(/\D/g, ''); const l = d.length === 11 && d.startsWith('1') ? d.slice(1) : d; return l.length === 10 ? `(${l.slice(0, 3)}) ${l.slice(3, 6)}-${l.slice(6)}` : p; };
const when = (iso: string) => new Date(iso).toLocaleString([], { weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const shortWhen = (iso: string) => new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const mdy = (iso: string) => { const d = new Date(iso); return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`; };
const dur = (s: number) => (s > 0 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');
const dobOf = (r: Row) => r.capturedFields?.find((f) => /(^|\.)(dob|dateOfBirth)$/i.test(f.token))?.value ?? '';
const needs = (r: Row) => (r.aiSummary || r.summary || '').trim();

/** One call as plain text, laid out for pasting into an EHR or an email. v12.10.15 (#1636): the text now lives in CallRecordSheet.callRecordText
 *  so the drawer's Copy call record writes exactly the same - this keeps the old name for Reports. */
export function recordText(r: Row, parts: Part[], practice: string,
  lists?: { callerTypes?: readonly TaxonomyItem[]; categories?: readonly TaxonomyItem[] } | null): string {
  // v12.10.17 (#1723 T120 follow-up): the record stores the workflow / intent KEY - the copy says the name
  return callRecordText(namedRecord(r, lists) as unknown as CrsRecord, parts, practice, formatReference(r.referenceNo));
}

/** What Show applied (#1572) - the report shows this until Show is pressed again. */
interface Applied {
  mode: 'one' | 'range'; pickedId: string | null; win: ReportWindow | null; assigned: string; status: 'all' | 'pending' | 'done';
  layout: 'list' | 'full'; parts: Part[]; key: string; run: string;
}

export default function CallRecordTab({ openCall = null, autoDo = null }: { openCall?: string | null; autoDo?: 'print' | 'pdf' | null } = {}) {
  const [mode, setMode] = useState<'one' | 'range'>('one');
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(openCall);
  const [pickOpen, setPickOpen] = useState(false);
  const [scope, setScope] = useState<DateScope>({ kind: 'preset', value: 'Yesterday' });
  const [assigned, setAssigned] = useState<string>('anyone');
  const [status, setStatus] = useState<'all' | 'pending' | 'done'>('all');
  const [layout, setLayout] = useState<'list' | 'full'>('list');
  const [parts, setParts] = useState<Part[]>(DEFAULT_PARTS);
  const [view, setView] = useState<'printed' | 'copied'>('printed');
  // v12.10.13 (#1564): from the drawer the one call is shown at once (the same key Show would make, so no 'changed' dot).
  const [applied, setApplied] = useState<Applied | null>(() => openCall
    ? { mode: 'one', pickedId: openCall, win: null, assigned: 'anyone', status: 'all', layout: 'list', parts: [...DEFAULT_PARTS], key: JSON.stringify(['one', openCall, DEFAULT_PARTS]), run: etTime() }
    : null);
  const autoDone = useRef(false);
  const [details, setDetails] = useState<Record<string, Row>>({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const findRef = useRef<HTMLSpanElement>(null);

  const org = useQuery<{ identity?: { displayName?: string; legalName?: string } }>({ queryKey: ['t2-org-name'], queryFn: () => api.get('/t2/settings/organization'), staleTime: 300_000 });
  const practice = org.data?.identity?.displayName || org.data?.identity?.legalName || sessionPracticeName() || 'Your practice';   // v12.10.15 (#1627)
  // v12.10.17 (#1723 T120 follow-up): the practice's caller types + categories (the list the sheet and Messages resolve against)
  const taxonomy = useQuery<{ categories?: TaxonomyItem[]; callerTypes?: TaxonomyItem[] }>({ queryKey: ['t2-personalization-cats'],
    queryFn: () => api.get('/t2/surface/personalization'), staleTime: 300_000 });
  const people = useQuery<{ id: number; name: string; self?: boolean }[]>({ queryKey: ['t2-assignees'], queryFn: () => api.get('/t2/security/assignees'), staleTime: 300_000 });
  const meId = (people.data ?? []).find((a) => a.self)?.id ?? null;
  const personName = (v: string) => v === 'anyone' ? 'anyone' : v === 'me' ? 'me' : v === 'none' ? 'nobody' : (people.data ?? []).find((a) => String(a.id) === v)?.name ?? 'someone';

  // the list: the last 90 days to find one call; the shown window for a range (the API also sends every open call - filtered below)
  const from90 = useMemo(() => { const d = startOfDay(new Date()); d.setDate(d.getDate() - 89); return d.toISOString(); }, []);
  const findList = useQuery<Envelope>({ queryKey: ['t2-callrecord-list', from90], queryFn: () => api.get(`/t2/surface/messages?from=${encodeURIComponent(from90)}`),
    enabled: mode === 'one' || applied?.mode === 'one', staleTime: 60_000 });
  const rangeFrom = applied?.mode === 'range' ? applied.win?.from ?? null : null;
  const rangeList = useQuery<Envelope>({ queryKey: ['t2-callrecord-list', rangeFrom, applied?.key], queryFn: () => api.get(`/t2/surface/messages?from=${encodeURIComponent(rangeFrom!)}`),
    enabled: !!rangeFrom, staleTime: 60_000 });
  // v12.10.14 (#1620): the practice's label colours for the dots on the card (the same list the Messages page uses)
  const labelColors = useMemo<Record<string, string>>(() => Object.fromEntries((findList.data?.availableLabels ?? rangeList.data?.availableLabels ?? []).map((l) => [l.name, l.color])),
    [findList.data, rangeList.data]);
  const rows90 = useMemo(() => (findList.data?.messages ?? []).filter((m) => !m.setupTestCall), [findList.data]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase(); const qd = q.replace(/\D/g, '');
    if (!q) return [] as Row[];
    return rows90.filter((m) => (m.name || '').toLowerCase().includes(q)
      || (qd.length >= 3 && ((m.phone || '').replace(/\D/g, '').includes(qd) || (m.referenceNo ?? '').replace(/\D/g, '').endsWith(qd)))
      || formatReference(m.referenceNo).toLowerCase().includes(q)).slice(0, 12);
  }, [query, rows90]);
  useEffect(() => {
    if (!pickOpen) return;
    const off = (e: MouseEvent) => { if (findRef.current && !findRef.current.contains(e.target as Node)) setPickOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [pickOpen]);

  // the pending filters, as a key - a white dot on Show when they differ from what is shown
  const pendingKey = mode === 'one' ? JSON.stringify(['one', pickedId, parts]) : JSON.stringify(['range', scopeKey(scope), assigned, status, layout, parts]);
  const dirty = !applied || applied.key !== pendingKey;
  function show() {
    setApplied({ mode, pickedId, win: mode === 'range' ? scopeWindow(scope) : null, assigned, status, layout, parts: [...parts], key: pendingKey, run: etTime() });
    setPickOpen(false);
  }

  // the shown calls (what Show applied)
  const selected: Row[] = useMemo(() => {
    if (!applied) return [];
    if (applied.mode === 'one') return applied.pickedId ? rows90.filter((r) => r.id === applied.pickedId) : [];
    if (!applied.win) return [];
    const f = new Date(applied.win.from), t = new Date(applied.win.to);
    return (rangeList.data?.messages ?? []).filter((m) => {
      if (m.setupTestCall) return false;
      const at = new Date(m.startedAt); if (at < f || at >= t) return false;
      if (applied.status === 'pending' && m.status === 'done') return false;
      if (applied.status === 'done' && m.status !== 'done') return false;
      if (applied.assigned === 'me' && (meId === null || m.assignedToUserId !== meId)) return false;
      if (applied.assigned === 'none' && (m.assignedToUserId || m.assignedTo)) return false;
      if (!['anyone', 'me', 'none'].includes(applied.assigned) && String(m.assignedToUserId ?? '') !== applied.assigned) return false;
      return true;
    }).sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  }, [applied, rows90, rangeList.data, meId]);
  const aParts = applied?.parts ?? parts;
  const listMode = applied?.mode === 'range' && applied.layout === 'list';
  const needDetail = !!applied && (!listMode || aParts.includes('notes') || aParts.includes('transcript') || aParts.includes('caller'));
  // the full record (notes, transcript, captured fields) of each shown call, read once - at most 100 for a range
  useEffect(() => {
    if (!needDetail) return;
    const missing = selected.slice(0, MAX_CALLS).filter((r) => !details[r.id]);
    if (missing.length === 0) return;
    let off = false;
    setLoadingDetails(true);
    (async () => {
      const got: Record<string, Row> = {};
      for (let i = 0; i < missing.length; i += 4) {
        const chunk = await Promise.all(missing.slice(i, i + 4).map((r) => api.get<Row>(`/t2/surface/messages/${encodeURIComponent(r.id)}`).catch(() => null)));
        chunk.forEach((r) => { if (r) got[r.id] = r; });
        if (off) return;
      }
      setDetails((d) => ({ ...d, ...got }));
      setLoadingDetails(false);
    })();
    return () => { off = true; };
  }, [selected, needDetail]);   // eslint-disable-line react-hooks/exhaustive-deps
  const full = selected.slice(0, MAX_CALLS).map((r) => (details[r.id] ? { ...r, ...details[r.id] } : r));
  const loading = !!applied && (applied.mode === 'one' ? findList.isLoading : rangeList.isLoading);
  const hasData = !!applied && !loading && full.length > 0;

  const has = (p: Part) => aParts.includes(p);
  const one = applied?.mode === 'one';
  const period = !applied ? '' : one ? (full[0] ? mdy(full[0].startedAt) : '') : applied.win?.period ?? '';
  const periodLabel = !applied ? '' : one ? (full[0] ? `${formatReference(full[0].referenceNo)} · ${shortWhen(full[0].startedAt)}` : '') : applied.win?.label ?? '';
  const viewWord = one ? 'One call' : applied?.layout === 'full' ? 'Full page per call' : 'List';
  // filter line (REPORT_STANDARD §3): only filters other than practice and dates, and only when not the default
  const filterLine: string[] = [];
  if (applied && !one) {
    if (applied.assigned !== 'anyone') filterLine.push(`Assigned to: ${personName(applied.assigned)}`);
    if (applied.status !== 'all') filterLine.push(`Status: ${applied.status === 'done' ? 'completed' : 'pending'}`);
  }
  if (applied && aParts.join() !== DEFAULT_PARTS.join()) filterLine.push(`Includes: ${PARTS.filter((p) => aParts.includes(p.key)).map((p) => p.label).join(', ')}`);
  const running = `${practice} · Call record · ${period}`;
  const wrapClass = 'vsr-root cr-root';
  const spec = () => (hasData && cardRef.current ? { node: cardRef.current, wrapClass, running, phi: true } : null);
  useBeforePrint(spec);

  async function audit(action: 'print' | 'pdf' | 'copy' | 'html') {
    try { await api.post('/t2/reports/call-record/audit', { action, calls: full.length, refs: full.map((r) => formatReference(r.referenceNo)), parts: aParts }); }
    catch { /* the action still happens; the API logs its own failure */ }
  }
  function print(action: 'print' | 'pdf') {
    const s = spec(); if (!s) return;
    void audit(action);
    if (action === 'pdf') toast('info', 'Save as PDF', 'Choose "Save as PDF" in the print window.');
    printReport(s);
  }
  // v12.10.13 (#1564): the drawer asked for Print / Download PDF - once the record (with its details) is on the page.
  useEffect(() => {
    if (!autoDo || autoDone.current || !hasData || loadingDetails || !openCall || !details[openCall]) return;
    autoDone.current = true;
    const t = window.setTimeout(() => print(autoDo), 300);
    return () => window.clearTimeout(t);
  }, [autoDo, hasData, loadingDetails, details, openCall]);   // eslint-disable-line react-hooks/exhaustive-deps
  function html() {
    if (!hasData || !cardRef.current) return;
    void audit('html');
    exportHtml({ node: cardRef.current, wrapClass, title: `Call record - ${practice} - ${periodLabel}`,
      fileName: `Call-record_${fileSafe(practice)}_${(full[0]?.startedAt ?? new Date().toISOString()).slice(0, 10)}.html`, prefixes: ['.vsr-', '.cr-', '.crs'] });
  }
  function listText() {
    return [`${practice} · Calls · ${full.length}`, ...full.map((r) => [formatReference(r.referenceNo), timeOf(r.startedAt),
      has('caller') ? `${r.name || ''} ${fmtPhone(r.phone)}`.trim() : '', has('summary') ? needs(r) : '', has('status') ? statusWord(r.status) : '',
      has('status') ? (r.assignedTo ?? 'Unassigned') : ''].filter(Boolean).join(' · '))].join('\n');
  }
  const plain = () => (listMode ? listText() : full.map((r) => recordText(r, aParts, practice, taxonomy.data)).join('\n\n----------\n\n'));
  async function copy() {
    try { await navigator.clipboard.writeText(plain()); toast('success', 'Copied', `${full.length} call record${full.length === 1 ? '' : 's'} - ready to paste.`); void audit('copy'); }
    catch { toast('error', 'Could not copy', 'Your browser blocked the clipboard - use Print instead.'); }
  }
  async function excel() {
    const columns = ['Ref', 'Date', 'Time', 'Caller', 'Phone', 'What the caller needs', 'Status', 'Assigned', 'Urgent', 'Labels', 'Reminder', 'Notes'];
    const sheetRows = full.map((r) => [formatReference(r.referenceNo), new Date(r.startedAt).toLocaleDateString(), timeOf(r.startedAt),
      has('caller') ? r.name : '', has('caller') ? fmtPhone(r.phone) : '', has('summary') ? needs(r) : '', has('status') ? statusWord(r.status) : '',
      has('status') ? (r.assignedTo ?? '') : '', has('status') && r.priority === 'urgent' ? 'Urgent' : '', has('labels') ? r.labels.join(', ') : '',
      has('reminder') ? (r.reminder ?? '') : '', has('notes') ? (r.notes ?? []).length : '']);
    try { await postAndSave('/t2/reports/xlsx', { title: `${practice} calls`, kind: 'call-record', calls: full.length, sheets: [{ name: 'Calls', columns, rows: sheetRows }] }, 'calls.xlsx'); }
    catch { toast('error', 'The Excel file was not made', 'Try again.'); }
  }

  return (
    <div className="vsr-root cr-root">
      <div className="vsr-bar">
        <span className="vsr-seg">
          <button type="button" className={mode === 'one' ? 'on' : ''} onClick={() => setMode('one')}>One call</button>
          <button type="button" className={mode === 'range' ? 'on' : ''} onClick={() => setMode('range')}>Date range</button>
        </span>
        {mode === 'one' ? (
          <span className="vsr-rel cr-findbox" ref={findRef}>
            <input className="cr-find" value={query} placeholder="#41-0082, a name or a phone number" aria-label="Find a call (the last 90 days)"
              onFocus={() => setPickOpen(true)} onChange={(e) => { setQuery(e.target.value); setPickedId(null); setPickOpen(true); }} />
            {pickOpen && query && !pickedId && (
              <div className="cr-pick" role="listbox">
                {matches.length === 0 ? <div className="cr-pick-none">{findList.isLoading ? 'Loading…' : 'No call found (the last 90 days).'}</div> : matches.map((m) => (
                  <button key={m.id} type="button" role="option" aria-selected={false}
                    onClick={() => { setPickedId(m.id); setPickOpen(false); setQuery(`${formatReference(m.referenceNo)} · ${m.name || fmtPhone(m.phone)} · ${shortWhen(m.startedAt)}`); }}>
                    <b>{formatReference(m.referenceNo)}</b> · {m.name || fmtPhone(m.phone)} · {shortWhen(m.startedAt)}
                  </button>
                ))}
              </div>
            )}
          </span>
        ) : (
          <>
            <ReportDates value={scope} onChange={setScope} />
            <select className="vsr-ctl" value={assigned} onChange={(e) => setAssigned(e.target.value)} aria-label="Assigned to">
              <option value="anyone">Assigned to: anyone</option>{meId !== null && <option value="me">Assigned to: me</option>}<option value="none">Assigned to: nobody</option>
              {(people.data ?? []).filter((a) => !a.self).map((a) => <option key={a.id} value={String(a.id)}>Assigned to: {a.name}</option>)}
            </select>
            <select className="vsr-ctl" value={status} onChange={(e) => setStatus(e.target.value as 'all' | 'pending' | 'done')} aria-label="Status">
              <option value="all">Status: all</option><option value="pending">Status: pending</option><option value="done">Status: completed</option>
            </select>
            <span className="cr-layout"><span className="vsr-lbl">Layout</span>
              <span className="vsr-seg">
                <button type="button" className={layout === 'list' ? 'on' : ''} onClick={() => setLayout('list')}>List</button>
                <button type="button" className={layout === 'full' ? 'on' : ''} onClick={() => setLayout('full')}>Full page per call</button>
              </span>
            </span>
          </>
        )}
        <ReportMenu label={<>Include · {parts.length} of 7 ▾</>} className="vsr-ctl" keepOpen align="left" wide>
          {PARTS.map((p) => (
            <label key={p.key} className="cr-chk"><input type="checkbox" checked={parts.includes(p.key)}
              onChange={(e) => setParts((cur) => (e.target.checked ? PARTS.map((x) => x.key).filter((k) => k === p.key || cur.includes(k)) : cur.filter((x) => x !== p.key)))} /> {p.label}{p.hint && <span>{p.hint}</span>}</label>
          ))}
        </ReportMenu>
        <ShowButton dirty={dirty} onClick={show} />
        <div className="vsr-acts">
          <button type="button" className="vsr-btn" disabled={!hasData} onClick={() => print('print')}>Print</button>
          <ReportMenu label="Export ▾" disabled={!hasData}>
            <button type="button" role="menuitem" className="vsr-mi" onClick={() => print('pdf')}>PDF - Save as PDF from the print window</button>
            {applied?.mode === 'range' && <button type="button" role="menuitem" className="vsr-mi" onClick={() => void excel()}>Excel (.xlsx) - date range</button>}
            <button type="button" role="menuitem" className="vsr-mi" onClick={html}>HTML - one page, opens anywhere</button>
            <div className="vsr-mh">Every print, export and copy is written to the audit log.</div>
          </ReportMenu>
          <button type="button" className="vsr-btn" disabled={!hasData} onClick={() => void copy()}>Copy</button>
        </div>
      </div>
      <div className="cr-warn">These records contain patient information. Each print, PDF, export and copy is written to the audit log.</div>

      <ReportDivider label={applied ? `Call record · ${periodLabel || (one ? 'no call picked' : '')} · ${practice}` : 'nothing shown yet - press Show'}>
        <span className="vsr-seg cr-viewseg">
          <button type="button" className={view === 'printed' ? 'on' : ''} onClick={() => setView('printed')}>As printed</button>
          <button type="button" className={view === 'copied' ? 'on' : ''} onClick={() => setView('copied')}>As copied</button>
        </span>
      </ReportDivider>
      <div className="vsr-card" ref={cardRef}>
        <PrintHeader practice={practice} report="Call record" view={one ? undefined : viewWord} period={period} run={applied?.run ?? ''} filters={filterLine} />{/* v12.10.14 (#1620): the note is gone - the footer says it */}
        {!applied ? (
          <ReportEmpty big={mode === 'one' ? 'Find a call and press Show' : 'Pick the dates and press Show'}>Nothing loads until you press Show once. After that, change any filter and press Show again - the white dot on Show means the record is not up to date with the filters.</ReportEmpty>
        ) : loading ? <ReportEmpty big="Loading…" />
          : full.length === 0 ? (
            one ? <ReportEmpty big="No call picked">Find a call by reference, name or phone, then press Show.</ReportEmpty>
              : <ReportEmpty big={`No calls for ${applied.win?.label ?? 'these dates'}`}>Pick other dates or filters above.</ReportEmpty>
          ) : view === 'copied' ? <pre className="cr-plain">{plain()}</pre> : (
            <>
              {loadingDetails && <div className="vsr-note vsr-noprint">Reading the full records…</div>}
              {selected.length > MAX_CALLS && <div className="vsr-note vsr-noprint">The first {MAX_CALLS} of {selected.length} calls are shown - pick fewer dates for the rest.</div>}
              {listMode ? (
                <>
                  <table className="cr-t">
                    <thead><tr><th>Ref</th><th>Time</th>{has('caller') && <th>Caller</th>}{has('summary') && <th>What the caller needs</th>}{has('status') && <th>Status</th>}{has('status') && <th>Assigned</th>}</tr></thead>
                    <tbody>{full.map((r) => (
                      <tr key={r.id}><td>{formatReference(r.referenceNo)}</td><td>{applied.win && applied.win.days > 1 ? shortWhen(r.startedAt) : timeOf(r.startedAt)}</td>
                        {has('caller') && <td>{r.name || '(no name given)'}<br />{fmtPhone(r.phone)}</td>}{has('summary') && <td>{needs(r)}</td>}
                        {has('status') && <td>{statusWord(r.status)}{r.priority === 'urgent' ? ' · Urgent' : ''}</td>}{has('status') && <td>{r.assignedTo ?? 'Unassigned'}</td>}</tr>
                    ))}</tbody>
                  </table>
                  <div className="cr-tot"><b>{full.length}</b> {full.length === 1 ? 'call' : 'calls'}</div>
                </>
              ) : full.map((r) => (
                // v12.10.14 (#1620): the one call record format (the drawer prints the same sheet)
                <div key={r.id} className="cr-call"><CallRecordSheet r={r as unknown as CrsRecord} parts={aParts} labelColors={labelColors} /></div>
              ))}
            </>
          )}
        <PrintFooter printed={applied?.run ?? ''} phi />
      </div>
    </div>
  );
}
